import React, { useState, useEffect } from 'react';
import { getStrategyTemplates } from '../../../services/StrategiesService';
import { processError } from '../../../services/ServiceUtils';
import { getCurrentMarket, subscribeToCurrentMarket, MARKET_FUTURES } from '../../../utils/MarketStorage';

const DIFFICULTY_BADGE = {
    'Iniciante':     'bg-success',
    'Intermediário': 'bg-warning text-dark',
    'Avançado':      'bg-danger',
};

const TEMPLATE_ICONS = {
    AI_TRADER:      '🤖',
    EMA_CROSSOVER:  '📈',
    REGIME_DCA:     '💰',
    AI_FULL:        '🚀',
    GRID_RANGE:     '🧱',
    FUTURES_SHORT:  '📉',
};

// Nota da casa (rating 1..5 + ratingNote explicando o porquê) — ver strategyTemplates.js.
// Tooltip via title nativo (hover) carrega o motivo; sem JS de bootstrap tooltip a inicializar.
function TemplateRating({ rating, ratingNote }) {
    if (!rating) return null;
    return (
        <span
            className='ms-1 small'
            style={{ color: '#f5a623', cursor: ratingNote ? 'help' : 'default', letterSpacing: 1 }}
            title={ratingNote || `Nota da casa: ${rating}/5`}
        >
            {'★'.repeat(rating)}<span className='text-muted'>{'★'.repeat(Math.max(0, 5 - rating))}</span>
        </span>
    );
}

/**
 * props:
 * - onSelect(template) — called when user clicks "Usar este template"
 */
function TemplateGallery({ onSelect }) {
    const [templates, setTemplates] = useState([]);
    const [error, setError] = useState('');
    const [market, setMarket] = useState(getCurrentMarket());

    useEffect(() => {
        getStrategyTemplates()
            .then(setTemplates)
            .catch(err => setError(processError(err)));
    }, []);
    useEffect(() => subscribeToCurrentMarket(setMarket), []);

    if (error) return <div className='alert alert-danger'>{error}</div>;

    // Só os templates do mercado atual (spot/futuros). Templates sem market = spot (default).
    const visible = templates.filter(t => (t.market || 'spot') === market);
    const isFutures = market === MARKET_FUTURES;

    return (
        <div>
            <p className='text-muted mb-3'>
                Escolha um modelo pré-configurado de <strong>{isFutures ? 'Futuros' : 'Spot'}</strong>. O sistema criará automaticamente todos os monitores, automações e templates de ordem necessários.
                Você poderá revisar e ajustar tudo antes de ativar.
            </p>
            {visible.length === 0 && (
                <div className='alert alert-info'>Nenhum template de {isFutures ? 'Futuros' : 'Spot'} disponível ainda.</div>
            )}
            <div className='row g-3'>
                {visible.map(t => (
                    <div className='col-md-4' key={t.id}>
                        <div className='card h-100 border shadow-sm'>
                            <div className='card-body d-flex flex-column'>
                                <div className='d-flex align-items-center mb-2'>
                                    <span className='fs-3 me-2'>{TEMPLATE_ICONS[t.id] || '📊'}</span>
                                    <div>
                                        <h6 className='mb-0'>{t.name}</h6>
                                        <span className={`badge ${DIFFICULTY_BADGE[t.difficulty] || 'bg-secondary'}`}>{t.difficulty}</span>
                                        <TemplateRating rating={t.rating} ratingNote={t.ratingNote} />
                                    </div>
                                </div>
                                <p className='text-muted small flex-grow-1'>{t.description}</p>
                                <div className='mb-2'>
                                    {(t.tags || []).map(tag => (
                                        <span key={tag} className='badge bg-light text-dark border me-1'>{tag}</span>
                                    ))}
                                </div>
                                <button
                                    type='button'
                                    className='btn btn-sm btn-outline-primary mt-auto'
                                    data-bs-toggle='modal'
                                    data-bs-target='#modalTemplateConfig'
                                    onClick={() => onSelect(t)}
                                >
                                    Usar este template
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default TemplateGallery;
